<script setup lang="ts">
import { computed, onMounted, reactive, watch } from 'vue'
import type { IConsent, IConsentGroup, IConsentsData, IConsentsSectionProps } from '../ts/types'
import { generateConsentId } from '../utils'

// Component props
const props = defineProps<IConsentsSectionProps>()

// Component events
const emit = defineEmits<{
    (event: 'update:data', data: IConsentsData, name: string, parentName?: string): void
}>()

// Main consents data state
const consentsData = reactive<IConsentsData>({})

/**
 * Emit updated consents data to parent component
 */
function emitConsentsData(name: string, parentName?: string): void {
    emit('update:data', consentsData, name, parentName)
}

/**
 * Check if a consent group has validation errors
 */
function hasConsentGroupErrors(consent: IConsent): boolean {
    if (!props.showErrors || !props.errors || !consent.consents) return false

    return consent.consents.some(subconsent => {
        const subConsentId = generateConsentId(props.formId, subconsent.name, consent.name)
        return props.errors?.[subConsentId]
    })
}

/**
 * Get component state based on validation
 */
function getComponentState(consentName: string, isGroup = false, parentName?: string): 'error' | 'default' {
    if (!props.showErrors || !props.errors) return 'default'

    if (isGroup) {
        const consent = props.consents.find(c => c.name === consentName)
        return hasConsentGroupErrors(consent as IConsent) ? 'error' : 'default'
    }

    const consentId = generateConsentId(props.formId, consentName, parentName)
    return props.errors[consentId] ? 'error' : 'default'
}

/**
 * Get error message for a consent
 */
function getErrorMessage(consentName: string, parentName?: string): string {
    if (!props.showErrors || !props.errors) return ''
    const consentId = generateConsentId(props.formId, consentName, parentName)
    return props.errors[consentId] || ''
}

/**
 * Open accordions that contain validation errors
 */
function openAccordionsWithErrors(): void {
    if (!props.showErrors || !props.errors) return

    Object.entries(consentsData).forEach(([name, value]) => {
        if (typeof value === 'object') {
            const consentGroup = value as IConsentGroup
            const consent = props.consents.find(c => c.name === name)

            if (consent?.consents) {
                const hasErrors = consent.consents.some(subconsent => {
                    const subConsentId = generateConsentId(props.formId, subconsent.name, name)
                    return props.errors?.[subConsentId]
                })

                if (hasErrors) {
                    consentGroup.isOpen = true
                }
            }
        }
    })
}

/**
 * Initialize consent data structure
 */
function initializeConsentsData(): void {
    props.consents.forEach((consent: IConsent) => {
        if (consent.consents) {
            initializeConsentGroup(consent)
        } else {
            consentsData[consent.name] = false
        }
    })
}

/**
 * Initialize a consent group with its subconsents
 */
function initializeConsentGroup(consent: IConsent): void {
    consentsData[consent.name] = {
        consents: {},
        isOpen: false,
        value: computed<boolean>({
            get: () => {
                const group = consentsData[consent.name] as IConsentGroup
                return Object.values(group.consents).every(value => value)
            },
            set: newValue => {
                const group = consentsData[consent.name] as IConsentGroup
                Object.keys(group.consents).forEach(key => {
                    group.consents[key] = newValue
                })
            },
        }),
    } as IConsentGroup

    // Initialize subconsents
    consent.consents?.forEach((subconsent: IConsent) => {
        ;(consentsData[consent.name] as IConsentGroup).consents[subconsent.name] = false
    })
}

// Watch for changes in validation state
watch([() => props.showErrors, () => props.errors], ([showErrors]) => {
    if (showErrors) {
        openAccordionsWithErrors()
    }
})

// Initialize data
initializeConsentsData()

// Emit initial data on component mount
onMounted(() => {
    emit('update:data', consentsData, '')
})
</script>

<template>
    <template v-for="(consent, i) in consents">
        <!-- Group Consent with Accordion -->
        <VAccordionItem
            v-if="consent.consents"
            :id="generateConsentId(formId, consent.name)"
            :key="formId + consent.name"
            v-model:is-open="(consentsData[consent.name] as IConsentGroup).isOpen"
            size="SMALL"
            :item="{
                value: 'all',
                isOpen: false,
            }"
            data-test="consents-accordion"
            :hide-divider="true"
            :state="getComponentState(consent.name, true)"
        >
            <!-- Accordion Header -->
            <template #header>
                <div @click.prevent>
                    <VCheckbox
                        :id="generateConsentId(formId, consent.name)"
                        v-model="(consentsData[consent.name] as IConsentGroup).value as boolean"
                        class="consent-checkbox"
                        :label="consent.legal"
                        :required="consent.required"
                        data-test="all-consent"
                        size="SMALL"
                        font-variant="paragraph"
                        :state="getComponentState(consent.name, true)"
                        @click.stop="emitConsentsData(consent.name)"
                    />
                </div>
            </template>

            <!-- Subconsents -->
            <div
                v-for="(subconsent, j) in consent.consents"
                :key="subconsent.name + consent.name"
                class="ml-md mt-base"
            >
                <VCheckbox
                    :id="generateConsentId(formId, subconsent.name, consent.name)"
                    v-model="(consentsData[consent.name] as IConsentGroup).consents[subconsent.name] as boolean"
                    class="consent-checkbox"
                    :label="subconsent.legal"
                    :required="subconsent.required"
                    :data-test="subconsent.name"
                    size="SMALL"
                    font-variant="paragraph"
                    :state="getComponentState(subconsent.name, false, consent.name)"
                    :error-message="getErrorMessage(subconsent.name, consent.name)"
                    @update:model-value="emitConsentsData(subconsent.name, consent.name)"
                />
            </div>
            <!-- Labels for single consent view -->
            <template v-if="consents.length === 1">
                <p
                    v-for="(label, labelIndex) in labels"
                    :key="`label-${labelIndex}`"
                    class="paragraph-sm text-neutral-tertiary mt-md lg:mt-lg"
                >
                    {{ label }}
                </p>
            </template>
        </VAccordionItem>
        <!-- Single Consent -->
        <VCheckbox
            v-else
            :id="generateConsentId(formId, consent.name)"
            :key="`single-${formId + consent.name}`"
            v-model="consentsData[consent.name] as boolean"
            class="mt-sm consent-checkbox"
            :class="{ 'mb-base': i === consents.length - 1 }"
            :label="consent.legal"
            :required="consent.required"
            :data-test="consent.name"
            size="SMALL"
            font-variant="paragraph"
            :state="getComponentState(consent.name)"
            :error-message="getErrorMessage(consent.name)"
            @click="emitConsentsData(consent.name)"
        />
    </template>
</template>

<style>
.consent-checkbox label {
  display: inline;
}
.consent-checkbox label span {
  display: inline;
}
.consent-checkbox label p {
  display: inline;
}
</style>
